import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import SEOHead from '../components/common/SEOHead';
import { getIndustryPage, getIndustryPages } from '../data/industrySeoPages';

const PHONE_DISPLAY = '073-487-71-19';
const PHONE_LINK = '+46734877119';

function trackEvent(name, params = {}) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', name, params);
  }
}

function SectionHeading({ eyebrow, title }) {
  return (
    <div className="mb-10 max-w-3xl">
      <p className="mb-3 font-semibold text-sky-700">{eyebrow}</p>
      <h2 className="text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">{title}</h2>
    </div>
  );
}

function InfoCards({ items }) {
  return (
    <div className="grid gap-5 md:grid-cols-3">
      {items.map(([title, text]) => (
        <article key={title} className="rounded-3xl border border-slate-200 bg-slate-50 p-7">
          <h3 className="text-xl font-bold text-slate-950">{title}</h3>
          <p className="mt-3 leading-7 text-slate-600">{text}</p>
        </article>
      ))}
    </div>
  );
}

export default function IndustrySeoLanding({ industry, language = 'sv' }) {
  const page = getIndustryPage(industry);
  if (!page) return <Navigate to={language === 'ar' ? '/ar' : '/'} replace />;

  const isArabic = language === 'ar';
  const content = page[language];
  const canonicalPath = isArabic ? page.arPath : page.svPath;
  const alternatePath = isArabic ? page.svPath : page.arPath;
  const canonical = `https://startawebb.se${canonicalPath}`;
  const hubPath = isArabic ? '/ar/hemsidor-for-smaforetag' : '/hemsidor-for-smaforetag';
  const contactPath = isArabic
    ? `/ar/kontakt?behov=hemsida&utm_source=seo&utm_campaign=${page.id}`
    : `/contact?behov=hemsida&utm_source=seo&utm_campaign=${page.id}`;
  const whatsappMessage = isArabic
    ? `مرحبًا StartaWebb، أريد موقعًا لنشاط ${page.labelAr} في السويد.`
    : `Hej StartaWebb! Jag vill diskutera en hemsida för ${page.labelSv.toLowerCase()}.`;
  const whatsappLink = `https://wa.me/46734877119?text=${encodeURIComponent(whatsappMessage)}`;
  const relatedPages = getIndustryPages().filter((item) => item.id !== page.id);

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: content.metaTitle,
      description: content.metaDescription,
      url: canonical,
      inLanguage: isArabic ? 'ar-SE' : 'sv-SE',
      areaServed: { '@type': 'Country', name: 'Sweden' },
      provider: {
        '@type': 'ProfessionalService',
        name: 'StartaWebb',
        url: 'https://startawebb.se',
        telephone: PHONE_LINK,
        email: 'info@startawebb.se',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Kristinehamn',
          addressRegion: 'Värmland',
          addressCountry: 'SE',
        },
      },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'SEK',
        price: '4900',
        description: isArabic
          ? 'سعر إرشادي لموقع شركة بسيط. السعر النهائي يحدد بعد معرفة العمل.'
          : 'Riktpris för en enkel företagshemsida. Slutpris lämnas efter avgränsning.',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: content.faqs.map(([question, answer]) => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: { '@type': 'Answer', text: answer },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: isArabic ? 'StartaWebb بالعربية' : 'StartaWebb',
          item: isArabic ? 'https://startawebb.se/ar' : 'https://startawebb.se/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: isArabic ? 'مواقع للشركات الصغيرة' : 'Hemsidor för småföretag',
          item: `https://startawebb.se${hubPath}`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: isArabic ? page.labelAr : page.labelSv,
          item: canonical,
        },
      ],
    },
  ];

  return (
    <div lang={isArabic ? 'ar' : 'sv'} dir={isArabic ? 'rtl' : 'ltr'} className="bg-white text-slate-950">
      <SEOHead
        title={content.metaTitle}
        description={content.metaDescription}
        canonical={canonical}
        keywords={content.keywords}
        language={isArabic ? 'ar' : 'sv'}
        locale={isArabic ? 'ar_SE' : 'sv_SE'}
        direction={isArabic ? 'rtl' : 'ltr'}
        alternateUrls={[
          { hrefLang: 'sv-SE', href: `https://startawebb.se${page.svPath}` },
          { hrefLang: 'ar-SE', href: `https://startawebb.se${page.arPath}` },
          { hrefLang: 'x-default', href: `https://startawebb.se${page.svPath}` },
        ]}
        ogAlternateLocales={[isArabic ? 'sv_SE' : 'ar_SE']}
        structuredData={structuredData}
      />

      <section className="relative overflow-hidden bg-gradient-to-br from-sky-50 via-white to-cyan-50">
        <div className="pointer-events-none absolute -right-28 -top-28 h-80 w-80 rounded-full bg-sky-200/40 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-36 -left-28 h-96 w-96 rounded-full bg-cyan-200/30 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 py-16 md:py-24">
          <div className="mb-7 flex flex-wrap items-center justify-between gap-3 text-sm font-semibold">
            <Link to={hubPath} className="text-sky-700 hover:text-sky-900">
              {isArabic ? 'كل صفحات الأنشطة' : 'Alla branschsidor'}
            </Link>
            <Link
              to={alternatePath}
              lang={isArabic ? 'sv' : 'ar'}
              dir={isArabic ? 'ltr' : 'rtl'}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-slate-700 transition hover:border-sky-300 hover:text-sky-800"
            >
              {isArabic ? 'Svenska' : 'العربية'}
            </Link>
          </div>

          <div className="grid items-center gap-10 lg:grid-cols-[1.08fr_0.92fr]">
            <div>
              <p className="mb-5 inline-flex rounded-full border border-sky-200 bg-white px-4 py-2 text-sm font-bold text-sky-800 shadow-sm">
                {content.eyebrow}
              </p>
              <h1 className="max-w-4xl text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-950 md:text-6xl">
                {content.title}
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-600 md:text-xl">{content.intro}</p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to={contactPath}
                  onClick={() => trackEvent('industry_cta_click', { industry: page.id, language, action: 'contact' })}
                  className="inline-flex items-center justify-center rounded-xl bg-sky-700 px-7 py-4 font-bold text-white shadow-lg shadow-sky-700/20 transition hover:-translate-y-0.5 hover:bg-sky-800"
                >
                  {isArabic ? 'اطلب اقتراحًا لموقع الشركة' : 'Få ett förslag för företaget'}
                </Link>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('industry_cta_click', { industry: page.id, language, action: 'whatsapp' })}
                  className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-7 py-4 font-semibold text-slate-800 transition hover:border-sky-300 hover:text-sky-800"
                >
                  {isArabic ? 'تواصل بالعربية على واتساب' : 'Skicka ett WhatsApp-meddelande'}
                </a>
              </div>
            </div>

            <aside className="rounded-3xl border border-sky-100 bg-white p-7 shadow-2xl shadow-sky-900/10 md:p-9">
              <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1.5 text-sm font-bold text-emerald-800">
                {isArabic ? 'سعر إرشادي، وليس باقة جاهزة' : 'Riktpris, inte ett låst paket'}
              </span>
              <h2 className="mt-5 text-2xl font-bold text-slate-950">
                {isArabic ? 'موقع شركة بسيط وواضح' : 'En enkel och tydlig företagshemsida'}
              </h2>
              <p className="mt-3 text-5xl font-extrabold tracking-tight text-sky-800">
                {isArabic ? 'حوالي 4,900 كرون' : 'Cirka 4 900 kr'}
              </p>
              <p className="mt-4 text-sm leading-7 text-slate-500">
                {isArabic
                  ? 'السعر النهائي والضريبة إن وجدت يذكران في عرض مكتوب بعد تحديد الصفحات والوظائف والمحتوى.'
                  : 'Slutpris och eventuell moms framgår i en skriftlig offert efter att sidor, funktioner och innehåll har avgränsats.'}
              </p>
              <div className="mt-6 space-y-3 border-t border-slate-100 pt-6 text-sm leading-7 text-slate-700">
                <p>✓ {isArabic ? 'متوافق مع الهاتف' : 'Mobilanpassad'}</p>
                <p>✓ {isArabic ? 'نموذج أو مسار تواصل واضح' : 'Tydligt kontakt- eller offertflöde'}</p>
                <p>✓ {isArabic ? 'مساعدة في المحتوى والنشر' : 'Hjälp med innehåll och publicering'}</p>
                <p>✓ {isArabic ? 'لا ضمانات مضللة للترتيب أو العملاء' : 'Inga vilseledande ranking- eller kundgarantier'}</p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-100 bg-white py-16">
        <div className="mx-auto max-w-6xl px-4">
          <SectionHeading
            eyebrow={content.fitTitle}
            title={isArabic ? 'صفحة مبنية حول قرار العميل، لا حول كلام تقني' : 'En sida byggd runt kundens beslut, inte tekniska modeord'}
          />
          <InfoCards items={content.fit} />
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div className="rounded-3xl bg-slate-900 p-8 text-white shadow-xl">
            <p className="text-sm font-bold uppercase tracking-[0.14em] text-sky-300">
              {isArabic ? 'محتوى الصفحة' : 'Sidans innehåll'}
            </p>
            <h2 className="mt-4 text-3xl font-bold leading-tight">{content.includedTitle}</h2>
            <div className="mt-7 space-y-3">
              {content.included.map((item) => (
                <p key={item} className="rounded-2xl bg-white/10 px-4 py-4 text-sm leading-7 text-slate-100">✓ {item}</p>
              ))}
            </div>
          </div>

          <div>
            <SectionHeading
              eyebrow={isArabic ? 'ما الذي يجعل الصفحة مفيدة؟' : 'Vad gör sidan användbar?'}
              title={content.detailTitle}
            />
            <div className="space-y-5">
              {content.details.map(([title, text]) => (
                <article key={title} className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm">
                  <h3 className="text-xl font-bold text-slate-950">{title}</h3>
                  <p className="mt-3 leading-8 text-slate-600">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-6xl px-4">
          <SectionHeading eyebrow={isArabic ? 'طريقة العمل' : 'Arbetssätt'} title={content.processTitle} />
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {content.process.map(([step, title, text]) => (
              <article key={step} className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-100 font-extrabold text-sky-800">{step}</span>
                <h3 className="mt-5 text-lg font-bold text-slate-950">{title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-100 bg-slate-50 py-20">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 lg:grid-cols-[1fr_0.82fr]">
          <div>
            <SectionHeading eyebrow={isArabic ? 'الأسئلة الشائعة' : 'Vanliga frågor'} title={content.faqTitle} />
            <div className="space-y-4">
              {content.faqs.map(([question, answer]) => (
                <details key={question} className="group rounded-2xl border border-slate-200 bg-white p-5 open:shadow-sm">
                  <summary className="cursor-pointer list-none font-bold text-slate-950">{question}</summary>
                  <p className="mt-4 leading-7 text-slate-600">{answer}</p>
                </details>
              ))}
            </div>
          </div>

          <aside className="rounded-3xl border border-sky-100 bg-sky-50 p-8 lg:sticky lg:top-24">
            <h2 className="text-2xl font-bold text-slate-950">{content.ctaTitle}</h2>
            <p className="mt-4 leading-8 text-slate-600">{content.ctaText}</p>
            <Link to={contactPath} className="mt-7 block rounded-xl bg-sky-700 px-6 py-4 text-center font-bold text-white transition hover:bg-sky-800">
              {isArabic ? 'أرسل معلومات المشروع' : 'Skicka projektinformationen'}
            </Link>
            <a href={`tel:${PHONE_LINK}`} dir="ltr" className="mt-3 block rounded-xl border border-sky-200 bg-white px-6 py-4 text-center font-bold text-slate-800 transition hover:border-sky-400">
              {PHONE_DISPLAY}
            </a>
          </aside>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-10 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="font-semibold text-sky-700">{isArabic ? 'أنشطة أخرى' : 'Andra branscher'}</p>
              <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-950">
                {isArabic ? 'صفحات متخصصة لأنواع شركات أخرى' : 'Branschanpassade sidor för fler verksamheter'}
              </h2>
            </div>
            <Link to={hubPath} className="font-bold text-sky-700 hover:text-sky-900">
              {isArabic ? 'عرض كل الأنشطة' : 'Visa alla branscher'}
            </Link>
          </div>
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {relatedPages.map((item) => (
              <Link
                key={item.id}
                to={isArabic ? item.arPath : item.svPath}
                className="rounded-3xl border border-slate-200 bg-slate-50 p-6 transition hover:-translate-y-1 hover:border-sky-200 hover:bg-white hover:shadow-lg"
              >
                <h3 className="text-xl font-bold text-slate-950">{isArabic ? item.labelAr : item.labelSv}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">{isArabic ? item.shortAr : item.shortSv}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
